/**
 * Resizable-columns primitive for plain <table> layouts.
 *
 * Usage (Step 11d · add-on: the short form — the table draws its own <colgroup> and gives every ResizableTh its drag
 * handler, so a header cell only needs its column index):
 *   const cols = useResizableColumns({ storageKey: "scoping", defaults: [260, 120, 120], mins: [160, 90, 90] });
 *
 *   <div className="overflow-x-auto">                  // the table scrolls sideways inside its card, never the page
 *     <ResizableTable cols={cols} className="text-xs">   // draws "Reset column widths" (EN + AR) in its <caption>;
 *                                                        // reset={false} + <ResetColumnWidths cols={cols} /> places it elsewhere
 *       <thead><tr>
 *         <ResizableTh index={0}>Head</ResizableTh> ...
 *       </tr></thead>
 *     </ResizableTable>
 *   </div>
 *
 * The long form (a hand-written <table> + <colgroup> and `onResizeStart` on every ResizableTh) still works unchanged.
 *
 * Storage: widths persist to localStorage under `resizable-cols:{storageKey}` (owner decision 01-Nov-2026: the key stays
 * per browser, so the screens that already resized keep their stored widths). Missing / invalid entries fall back to
 * `defaults`. Widths are a browser convenience only, never sent to the server.
 *
 * Constraints: minimum column width 40px; no maximum. Optional per-column
 * minimums (array, same order as `defaults`) raise single columns: `mins`
 * (R54-1, ITGC Status) or its alias `minWidths` (Batch 35 · B35-8, vouching
 * sheet); a stored narrower width is raised to it on load and a drag never
 * goes below it. Without them nothing changes. Global
 * mousemove/mouseup listeners attach on drag start and detach on
 * drag end — no leak.
 *
 * Step 11d · add-on: `resetWidths()` clears the stored widths (back to `defaults`); a double-click on a column's handle
 * fits the column to its widest visible cell (capped at FIT_MAX_PX, never below its minimum); in a right-to-left page the
 * handle sits on the column's LEFT edge (logical `end-0`) and dragging left widens the column.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const MIN_COL_PX = 40;
const FIT_MAX_PX = 520;      // a double-click fit never makes one column wider than this (a long description wraps instead)
const FIT_PAD_PX = 14;       // room for the handle and the cell's own padding rounding

// R54-1 · optional per-column minimum (``mins[i]``, else 40 px): a column whose text must fit (ITGC Status) never goes below it
const minOf = (mins, i) => Math.max(MIN_COL_PX, (mins && typeof mins[i] === "number") ? mins[i] : 0);

const storeKey = (key) => `resizable-cols:${key}`;

const readStored = (key, defaults, mins) => {
  if (!key) return defaults;
  try {
    const raw = localStorage.getItem(storeKey(key));
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== defaults.length) return defaults;
    return parsed.map((n, i) =>
      typeof n === "number" && n >= MIN_COL_PX ? Math.max(n, minOf(mins, i)) : defaults[i]);
  } catch {
    return defaults;
  }
};

const writeStored = (key, widths) => {
  if (!key) return;
  try { localStorage.setItem(storeKey(key), JSON.stringify(widths)); }
  catch { /* quota / privacy mode — silent */ }
};

const clearStored = (key) => {
  if (!key) return;
  try { localStorage.removeItem(storeKey(key)); }
  catch { /* privacy mode — silent */ }
};

// the text of the control / handle in the page's language; a test that mocks the i18n context without the key gets English
const useI18nSafe = typeof useI18n === "function" ? useI18n : () => null;
const useColText = () => {
  const i18n = useI18nSafe();
  return (key, fallback) => {
    const v = i18n && typeof i18n.t === "function" ? i18n.t(`colWidths.${key}`) : null;
    return typeof v === "string" && v && v !== `colWidths.${key}` ? v : fallback;
  };
};

// every cell of a table with the visual column it starts at and its colSpan — rowSpan cells above (two-row headers, W9
// totals) and hidden hook columns (vouching sheet) counted — so a fit measures the right cells
const listOf = (el, k) => (el && el[k] ? Array.from(el[k]) : []);   // a table's rows / a row's cells (HTML collections)
const gridOf = (table) => {
  const out = [];
  const taken = [];
  listOf(table, "rows").forEach((row, r) => {
    taken[r] = taken[r] || [];
    let col = 0;
    listOf(row, "cells").forEach((c) => {
      while (taken[r][col]) col += 1;
      const span = c.colSpan || 1;
      const down = c.rowSpan || 1;
      for (let dr = 0; dr < down; dr += 1) {
        taken[r + dr] = taken[r + dr] || [];
        for (let dc = 0; dc < span; dc += 1) taken[r + dr][col + dc] = true;
      }
      out.push({ cell: c, col, span });
      col += span;
    });
  });
  return out;
};

const visualCol = (cell) => {
  const hit = gridOf(cell.closest ? cell.closest("table") : null).find((g) => g.cell === cell);
  return hit ? hit.col : 0;
};

// the widest visible cell of one visual column, measured on one line (nowrap) off-screen; 0 when nothing can be measured
export function measureColumn(table, col) {
  if (!table || typeof document === "undefined") return 0;
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = "position:absolute;visibility:hidden;left:-100000px;top:0;white-space:nowrap;width:auto;";
  document.body.appendChild(probe);
  let widest = 0;
  try {
    gridOf(table).filter((g) => g.col === col && g.span === 1).forEach(({ cell }) => {
      const cs = window.getComputedStyle(cell);
      probe.style.font = cs.font;
      probe.style.fontSize = cs.fontSize;
      probe.style.fontWeight = cs.fontWeight;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.textTransform = cs.textTransform;
      probe.innerHTML = cell.innerHTML;
      probe.querySelectorAll("[role=separator]").forEach((x) => x.remove());
      const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      widest = Math.max(widest, Math.ceil(probe.scrollWidth + pad));
    });
  } finally {
    probe.remove();
  }
  return widest;
}

export function useResizableColumns({ storageKey, defaults, mins: minsArg, minWidths }) {
  const mins = minsArg || minWidths;      // R54-1 ``mins`` / B35-8 ``minWidths``: the same option, two names
  const [colWidths, setColWidths] = useState(() => readStored(storageKey, defaults, mins));
  const minsRef = useRef(mins);
  minsRef.current = mins;
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;
  const drag = useRef(null); // {index, startX, startWidth}

  // Reset to defaults if the caller's column count changes (schema change).
  useEffect(() => {
    if (colWidths.length !== defaults.length) {
      setColWidths(readStored(storageKey, defaults, minsRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.length]);

  // Step 11d · add-on: a table whose key changes (one component, another column set) shows that key's widths
  const keyRef = useRef(storageKey);
  useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    setColWidths(readStored(storageKey, defaultsRef.current, minsRef.current));
  }, [storageKey]);

  const onResizeStart = useCallback((e, index) => {
    e.preventDefault();
    // B35-7 · 5: in a right-to-left page the handle sits on the column's LEFT edge (``end-0``), so dragging left widens it
    const host = e.currentTarget && e.currentTarget.closest ? e.currentTarget.closest('[dir="rtl"],[dir="ltr"]') : null;
    const dir = (host && host.getAttribute("dir"))
      || (typeof document !== "undefined" && document.documentElement.getAttribute("dir")) || "ltr";
    drag.current = {
      index,
      startX: e.clientX,
      startWidth: colWidths[index],
      sign: dir === "rtl" ? -1 : 1,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colWidths]);

  useEffect(() => {
    const onMove = (e) => {
      if (!drag.current) return;
      const delta = (e.clientX - drag.current.startX) * (drag.current.sign || 1);
      const { index, startWidth } = drag.current;      // read now: the updater may run after mouseup cleared drag.current
      const width = Math.max(minOf(minsRef.current, index), startWidth + delta);
      setColWidths((prev) => {
        const next = prev.slice();
        next[index] = width;
        return next;
      });
    };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setColWidths((w) => { writeStored(storageKey, w); return w; });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [storageKey]);

  // Step 11d · add-on 4: "Reset column widths" clears this table's stored widths and shows the defaults again
  const resetWidths = useCallback(() => {
    setColWidths(defaultsRef.current);
    clearStored(storageKey);
  }, [storageKey]);

  // Step 11d · add-on 5: set one column (a double-click fit), kept within its minimum, stored like a drag
  const setColumnWidth = useCallback((index, width) => {
    setColWidths((prev) => {
      if (index < 0 || index >= prev.length || !(width > 0)) return prev;
      const next = prev.slice();
      next[index] = Math.max(minOf(minsRef.current, index), Math.round(width));
      writeStored(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const fitColumn = useCallback((index, fromEl) => {
    const cell = fromEl && fromEl.closest ? fromEl.closest("th,td") : null;
    const table = cell && cell.closest ? cell.closest("table") : null;
    if (!table) return;
    const measured = measureColumn(table, visualCol(cell));
    if (measured > 0) setColumnWidth(index, Math.min(FIT_MAX_PX, measured + FIT_PAD_PX));
  }, [setColumnWidth]);

  // the existing ResizableTh call sites pass only ``onResizeStart``; the fit rides on it so they get the double-click too
  onResizeStart.fitColumn = fitColumn;

  // the render where the key or the column count has just changed already shows that set's widths (the effects above then
  // store them in state), so a table never draws one frame with the old set
  const live = (colWidths.length === defaults.length && keyRef.current === storageKey)
    ? colWidths : readStored(storageKey, defaults, mins);
  const totalWidth = live.reduce((a, b) => a + b, 0);
  const isDefault = live.length === defaults.length && live.every((w, i) => w === defaults[i]);

  return { colWidths: live, onResizeStart, resetWidths, setColumnWidth, fitColumn, totalWidth, isDefault, storageKey };
}

const ColsCtx = createContext(null);

/* Step 11d · add-on — a <table> with fixed layout, the hook's widths in a <colgroup>, at least the width of its box (so a
   narrow table still fills its card) and the drag handler for every ResizableTh inside it. Any other prop goes to <table>.
   The surrounding box keeps the sideways scroll (overflow-x-auto / table-sticky-box), so the page itself never scrolls;
   a table without such a box passes ``wrap`` and gets one (never around a sticky-header box: the header must keep sticking). */
export function ResizableTable({ cols, className = "", style, children, reset = true, wrap = false, ...rest }) {
  const table = (
      <table {...rest} className={className} data-resizable={cols.storageKey || ""}
             style={{ ...style, tableLayout: "fixed", width: cols.totalWidth, minWidth: "100%" }}>
        {/* add-on 4: the table's own "Reset column widths" control, above the header on the reading-start side (left in
            English, right in Arabic) so it is in view before any sideways scroll; never printed */}
        {reset && <ResetCaption cols={cols} />}
        <colgroup>
          {cols.colWidths.map((w, i) => (
            <col key={i} style={{ width: w }} data-testid={`rcol-${cols.storageKey}-${i}`} />
          ))}
        </colgroup>
        {children}
      </table>
  );
  // ``wrap``: a table with no scroll box of its own gets one, so a wide table scrolls inside its card, never the page
  return (
    <ColsCtx.Provider value={cols}>
      {wrap ? <div className="max-w-full overflow-x-auto" data-testid={`rtable-wrap-${cols.storageKey}`}>{table}</div> : table}
    </ColsCtx.Provider>
  );
}

/* Step 11d · add-on 4 — the Reset control as the first child of a hand-written <table> (the screens that resized before) */
export function ResetCaption({ cols }) {
  return (
    <caption className="caption-top print:hidden" style={{ textAlign: "start" }}>
      <ResetColumnWidths cols={cols} align="start" />
    </caption>
  );
}

/* Step 11d · add-on 4 — the small "Reset column widths" control (EN + AR) of one resizable table. */
export function ResetColumnWidths({ cols, className = "", testid, align = "end" }) {
  const tx = useColText();
  const label = tx("reset", "Reset column widths");
  return (
    <div className={`flex ${align === "start" ? "justify-start" : "justify-end"} ${className}`}>
      <button type="button" onClick={cols.resetWidths} title={tx("resetHint", "Back to the default widths of this table")}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
              data-testid={testid || `reset-cols-${cols.storageKey}`} data-default={cols.isDefault ? "1" : "0"}>
        <RotateCcw className="h-3 w-3" aria-hidden="true" />
        {label}
      </button>
    </div>
  );
}

export function ResizableTh({ index, onResizeStart, className = "",
                              children, ...rest }) {
  const cols = useContext(ColsCtx);
  const tx = useColText();
  const start = onResizeStart || (cols && cols.onResizeStart);
  const fit = (start && start.fitColumn) || (cols && cols.fitColumn);
  // add-on: ``break-words`` — a long header word wraps at a narrow width, never hidden under the next column
  return (
    <th
      {...rest}
      className={`relative select-none break-words ${className}`}
      data-col-index={index}
    >
      {children}
      {/* Always-visible drag handle: thin vertical bar at the end edge
          of every column header (the right edge; the left edge in
          Arabic). Slightly darker on hover + during active drag. Wide
          6px hit-target for easy grabbing with a 2px visual line
          centred inside it. Double-click fits the column. */}
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label={tx("handle", "Resize column — drag to adjust width")}
        title={tx("handleHint", "Drag to resize · double-click to fit")}
        onMouseDown={(e) => start && start(e, index)}
        onDoubleClick={(e) => { e.preventDefault(); if (fit) fit(index, e.currentTarget); }}
        className="absolute end-0 top-1 bottom-1 w-1.5 cursor-col-resize flex items-center justify-center group/resizer hover:bg-primary/10 active:bg-primary/20 rounded-sm transition-colors"
        data-testid={`col-resize-handle-${index}`}
      >
        {/* The always-visible visual line. Uses foreground/25 so it
            reads as a divider, foreground/60 on hover so the drag
            affordance is obvious. */}
        <span
          aria-hidden="true"
          className="block h-3/5 w-px bg-foreground/25
                      group-hover/resizer:w-0.5 group-hover/resizer:bg-foreground/60
                      group-active/resizer:bg-primary
                      transition-all"
        />
      </span>
    </th>
  );
}
